import { routePaths, unprotectedRoutes } from "routing";

import { getPathNamesFromRoutes } from "routing";
import { isEnvLocalOnly } from "utils/environment/environmentUtils";
import { isNullOrUndefOrEmpty } from "@civicscience/chops";
import { protectedRoutes } from "routing/routes";

const rootPaths = ["", "/"];
const loginPath = `${routePaths.LOGIN}`;
const logOutErrorParam = "loggedOutError";
const fromUrlParam = "fromUrl";
const returnToParam = "return_to";
// widget admin is served from the same origin as InsightStore, under the /wa path
const allowedReturnToHostnames = [
  "ask.civicscience.com",
  "insightstore.civicsciencedev.com",
  "insightstore.civicscience.com",
];

const getUnauthorizedPathNames = (): string[] => getPathNamesFromRoutes(unprotectedRoutes ?? []);
const getAuthorizedPathNames = (): string[] => getPathNamesFromRoutes(protectedRoutes);

/**
 *
 * @param fromUrl - href to create fromUrl base64 param
 */
const getLoggedOutUrl = (fromUrl?: string): string => {
  const fromUrlToTest = fromUrl || window.location.href;
  const fromUrlToBase64 = isNullOrUndefOrEmpty(fromUrlToTest) ? null : window.btoa(fromUrlToTest);
  const fromUrlParams = fromUrlToBase64 ? `&${fromUrlParam}=${fromUrlToBase64}` : "";
  return `${loginPath}?${logOutErrorParam}=true${fromUrlParams}`;
};

const hasLoggedOutError = (url?: string): boolean => {
  const urlToTest = url || window.location.href;
  const { searchParams } = new URL(urlToTest);
  return searchParams.get(logOutErrorParam) === "true";
};

/**
 *
 * @return (string|null) - fromUrl Url.searchParam converted from base64 string OR null
 */
const getFromUrlParam = (url?: string): string | null => {
  const urlToTest = url || window.location.href;
  const { searchParams } = new URL(urlToTest);
  const hasFromUrl = searchParams.has(fromUrlParam);
  const fromUrlParamForBase64 = hasFromUrl ? searchParams.get(fromUrlParam) : null;
  return hasLoggedOutError(url) && fromUrlParamForBase64 ? window.atob(fromUrlParamForBase64) : null;
};

/**
 * Only the domain is checked - any path, query string, or hash on an allowed domain is permitted.
 *
 * @param url - a candidate return_to url to validate
 */
const isAllowedReturnToUrl = (url: URL): boolean => allowedReturnToHostnames.includes(url.hostname);

/**
 *
 * @param search - a location.search string (e.g. "?return_to=https://example.com") to read the return_to param from
 * @return (string|null) - the return_to url IF it is a valid, allowed https url, otherwise null
 */
const getReturnToUrl = (search?: string): string | null => {
  const searchToTest = search ?? window.location.search;
  const returnTo = new URLSearchParams(searchToTest).get(returnToParam);

  if (!returnTo) return null;

  try {
    const url = new URL(returnTo);

    // local development bypasses the hostname allowlist (e.g. widget admin running on localhost), but the
    // protocol must still be http/https - never allow something like a javascript: url through
    if (isEnvLocalOnly() && /^https?:$/.test(url.protocol)) return returnTo;

    return url.protocol === "https:" && isAllowedReturnToUrl(url) ? returnTo : null;
  } catch {
    return null;
  }
};

/**
 *
 * @param url - an external url to navigate the browser to (full page navigation)
 */
const navigateTo = (url: string): void => {
  window.location.assign(url);
};

/**
 * The url to send the user to immediately after a successful login instead of into the app
 * (e.g. AskCS's return_to auto-login redirect), if any.
 *
 * @param pathname - a location.pathname to check against the login route
 * @param search - a location.search string to read the return_to param from
 */
const getPostLoginRedirectUrl = (pathname?: string, search?: string): string | null => {
  const pathnameToTest = pathname ?? window.location.pathname;
  return pathnameToTest === loginPath ? getReturnToUrl(search) : null;
};

const getLoggedOutError = (url?: string) => {
  const fromUrl = getFromUrlParam(url || window.location.href);
  const showFromUrl = true; //NOTE: If we want to show the url from where the logout took place.
  // We can turn this off based on environment if we want to.
  const fromUrlMessaging = showFromUrl && fromUrl ? " accessing @ " + fromUrl : "";
  return `Something went wrong${fromUrlMessaging}. Please login and try again.`;
};

//NOTE: Checking IF we are already @ an authenticated route OR IF user was going to the root path
const canRedirectToLoginFrom401 = (urlPathName?: string) => {
  const unauthorizedPathNames = getUnauthorizedPathNames();
  const currentPathName = urlPathName || window.location.pathname;
  const isNotRootPath = !rootPaths.includes(currentPathName);
  return !unauthorizedPathNames.some((x) => currentPathName.startsWith(x)) && isNotRootPath;
};

export default {
  loginPath,
  logOutErrorParam,
  fromUrlParam,
  returnToParam,
  hasLoggedOutError,
  getLoggedOutError,
  getLoggedOutUrl,
  getFromUrlParam,
  getReturnToUrl,
  isAllowedReturnToUrl,
  navigateTo,
  getPostLoginRedirectUrl,
  getPathNamesFromRoutes,
  getUnauthorizedPathNames,
  getAuthorizedPathNames,
  canRedirectToLoginFrom401,
};
